import { decrireFigureV2 } from './decrire-v2.js';
import { mesureAngle, normaliserFigureV2 } from './figure-v2.js';
import { formaterNombre, projete } from './geometrie.js';
/** L'aimantation d'un glisser, quand la figure n'en déclare pas : un dixième d'unité. */
export const PAS_DYNAMIQUE_DEFAUT = 0.1;
/** Une position à la précision du dessin : le bruit flottant ne se lit pas. */
const propre = (v) => Number(v.toFixed(6));
const nb = (v) => formaterNombre(Number(v.toFixed(2))).replace('-', '−');
const estObjet = (v) => typeof v === 'object' && v !== null;
/** La figure brute, normalisée, si c'est un plan ; `null` sinon. */
export function planDynamique(brut) {
    const n = normaliserFigureV2(brut);
    return 'figure' in n && n.figure.espace === 'plan' ? n.figure : null;
}
/** Les objets bruts d'une figure. */
function objetsBruts(brut) {
    const objets = estObjet(brut) ? brut.objets : undefined;
    return Array.isArray(objets) ? objets.filter(estObjet) : [];
}
/** Les points NOMMÉS de la figure brute, par nom. */
export function pointsNommes(brut) {
    const sortie = new Map();
    for (const o of objetsBruts(brut)) {
        if (o.type !== 'point' || typeof o.nom !== 'string')
            continue;
        if (typeof o.x === 'number' && typeof o.y === 'number')
            sortie.set(o.nom, { x: o.x, y: o.y });
    }
    return sortie;
}
/** Une référence à un point : son nom, ou ses coordonnées. */
function lireRef(ref, points) {
    if (typeof ref === 'string')
        return points.get(ref) ?? null;
    if (estObjet(ref) && typeof ref.x === 'number' && typeof ref.y === 'number') {
        return { x: ref.x, y: ref.y };
    }
    return null;
}
/** Les deux bouts d'un segment ou d'une droite brute (par noms ou par coordonnées). */
function boutsDe(o, points) {
    if (typeof o.x1 === 'number' &&
        typeof o.y1 === 'number' &&
        typeof o.x2 === 'number' &&
        typeof o.y2 === 'number') {
        return [
            { x: o.x1, y: o.y1 },
            { x: o.x2, y: o.y2 },
        ];
    }
    const de = lireRef(o.de, points);
    const a = lireRef(o.a, points);
    return de === null || a === null ? null : [de, a];
}
/** Le rang que `sur` désigne (« e:3 » → 3) ; `null` : illisible. */
function rangDe(sur) {
    const m = /^e:(\d+)$/u.exec(sur);
    return m === null ? null : Number(m[1]);
}
/** Le point `p` ramené sur l'objet que `sur` désigne ; `null` : l'objet n'est pas lisible. */
function projeterSur(brut, sur, p, points) {
    const rang = rangDe(sur);
    const o = rang === null ? undefined : objetsBruts(brut)[rang];
    if (o === undefined)
        return null;
    if (o.type === 'cercle') {
        const c = lireRef(o.centre, points);
        if (c === null || typeof o.rayon !== 'number' || !(o.rayon > 0))
            return null;
        const dx = p.x - c.x;
        const dy = p.y - c.y;
        const d = Math.hypot(dx, dy);
        /* Au centre même, aucune direction : le point garde la droite du cercle. */
        if (d < 1e-12)
            return { x: c.x + o.rayon, y: c.y };
        return { x: c.x + (dx * o.rayon) / d, y: c.y + (dy * o.rayon) / d };
    }
    if (o.type === 'segment' || o.type === 'droite') {
        const b = boutsDe(o, points);
        if (b === null)
            return null;
        const q = projete(p, b[0], b[1]);
        if (q === null)
            return null;
        if (o.type === 'droite')
            return q;
        /* Un segment : le projeté est borné à ses deux bouts. */
        const ux = b[1].x - b[0].x;
        const uy = b[1].y - b[0].y;
        const t = ((q.x - b[0].x) * ux + (q.y - b[0].y) * uy) / (ux * ux + uy * uy);
        const s = Math.min(Math.max(t, 0), 1);
        return { x: b[0].x + s * ux, y: b[0].y + s * uy };
    }
    return null;
}
/**
 * Déplace un point de la figure. La position est bornée à la fenêtre du
 * repère, aimantée au pas (au glisser), puis ramenée sur son objet s'il est
 * déclaré `sur` l'un d'eux. `null` : le point n'est pas déplaçable, ou la
 * figure, à cette position, ne se dessine plus — le point reste alors où il
 * était (une figure intraçable est refusée, jamais dessinée à moitié).
 */
export function placerPointDynamique(brut, dynamique, nom, cible, options = {}) {
    const decl = dynamique.points.find((p) => p.nom === nom);
    const plan = planDynamique(brut);
    if (decl === undefined || plan === null || !estObjet(brut))
        return null;
    if (!Number.isFinite(cible.x) || !Number.isFinite(cible.y))
        return null;
    const r = plan.repere;
    let p = {
        x: Math.min(Math.max(cible.x, r.xMin), r.xMax),
        y: Math.min(Math.max(cible.y, r.yMin), r.yMax),
    };
    if (options.aimanter !== false) {
        const pas = dynamique.pas ?? PAS_DYNAMIQUE_DEFAUT;
        p = { x: Math.round(p.x / pas) * pas, y: Math.round(p.y / pas) * pas };
    }
    if (decl.sur !== undefined) {
        const q = projeterSur(brut, decl.sur, p, pointsNommes(brut));
        if (q === null)
            return null;
        p = q;
    }
    const position = { x: propre(p.x), y: propre(p.y) };
    const objets = objetsBruts(brut).map((o) => o.type === 'point' && o.nom === nom ? { ...o, x: position.x, y: position.y } : o);
    const suivant = { ...brut, objets };
    return planDynamique(suivant) === null ? null : { brut: suivant, position };
}
/**
 * Le centre du cercle sur lequel un point est déclaré ; `null` : il n'est pas
 * sur un cercle. Au clavier, un tel point TOURNE (← → : 5°, Maj : 1°).
 */
export function centreDuCercleDe(brut, decl) {
    if (decl.sur === undefined)
        return null;
    const rang = rangDe(decl.sur);
    const o = rang === null ? undefined : objetsBruts(brut)[rang];
    if (o === undefined || o.type !== 'cercle')
        return null;
    return lireRef(o.centre, pointsNommes(brut));
}
/** Fait tourner un point déclaré sur un cercle, de `degres` (positif : sens antihoraire). */
export function tournerPointDynamique(brut, dynamique, nom, degres) {
    const decl = dynamique.points.find((p) => p.nom === nom);
    const centre = decl === undefined ? null : centreDuCercleDe(brut, decl);
    const p = pointsNommes(brut).get(nom);
    if (centre === null || p === undefined)
        return null;
    const t = (degres * Math.PI) / 180;
    const dx = p.x - centre.x;
    const dy = p.y - centre.y;
    return placerPointDynamique(brut, dynamique, nom, {
        x: centre.x + dx * Math.cos(t) - dy * Math.sin(t),
        y: centre.y + dx * Math.sin(t) + dy * Math.cos(t),
    }, { aimanter: false });
}
/** Les mesures, sur la figure telle qu'elle est maintenant. Une mesure dont un point manque se tait. */
export function mesurerFigureDynamique(figure, mesures) {
    const points = new Map();
    for (const o of figure.objets)
        if (o.type === 'point' && o.nom !== '')
            points.set(o.nom, o);
    /* « L'angle C » quand C n'est le sommet que d'un seul angle mesuré ; sinon « l'angle ACB ». */
    const sommets = mesures.flatMap((m) => (m.type === 'angle' ? [m.sommet] : []));
    const sortie = [];
    for (const m of mesures) {
        if (m.type === 'longueur') {
            const de = points.get(m.de);
            const a = points.get(m.a);
            if (de === undefined || a === undefined)
                continue;
            const texte = nb(Math.hypot(a.x - de.x, a.y - de.y));
            sortie.push({
                mesure: m,
                etiquette: `${m.de}${m.a}`,
                texte,
                phrase: `Le segment [${m.de}${m.a}] mesure ${texte}.`,
            });
        }
        else if (m.type === 'angle') {
            const s = points.get(m.sommet);
            const de = points.get(m.de);
            const a = points.get(m.a);
            if (s === undefined || de === undefined || a === undefined)
                continue;
            const texte = `${String(Math.round(mesureAngle(s, de, a)))}°`;
            const seul = sommets.filter((x) => x === m.sommet).length === 1;
            const nom = seul ? m.sommet : `${m.de}${m.sommet}${m.a}`;
            sortie.push({
                mesure: m,
                etiquette: `Angle ${nom}`,
                texte,
                phrase: `L’angle ${nom} mesure ${texte}.`,
            });
        }
        else {
            const p = points.get(m.point);
            if (p === undefined)
                continue;
            const texte = `(${nb(p.x)}\u00a0;\u00a0${nb(p.y)})`;
            sortie.push({
                mesure: m,
                etiquette: m.point,
                texte,
                phrase: `Le point ${m.point} est en ${texte}.`,
            });
        }
    }
    return sortie;
}
/**
 * La description de la figure qui SUIT SON ÉTAT (SPEC § 6.10.6) : ce que
 * `decrireFigureV2` dit de la figure, puis chaque mesure en phrase.
 */
export function decrireFigureDynamique(figure, mesures) {
    const phrases = mesurerFigureDynamique(figure, mesures).map((m) => m.phrase);
    return [decrireFigureV2(figure), ...phrases].join(' ');
}
/** « C est en (3 ; 4). » : la position d'un point déplaçable, en phrase. */
export function decrirePositionDynamique(nom, p) {
    return `${nom} est en (${nb(p.x)}\u00a0;\u00a0${nb(p.y)}).`;
}
/* ─────────────────────────────── l'import ─────────────────────────────── */
/** Les noms de points qu'un objet brut cite. */
function nomsCites(o) {
    const refs = [o.de, o.a, o.centre, o.sommet];
    if (Array.isArray(o.sommets))
        refs.push(...o.sommets);
    return refs.filter((r) => typeof r === 'string');
}
/**
 * Ce que l'import refuse d'une figure dynamique. Une figure qui MENTIRAIT
 * après un glisser est refusée : une cote écrite (« 5 cm ») ou une marque
 * d'angle droit sur un point qui bouge ne suivent pas la figure — c'est une
 * MESURE qu'il faut alors déclarer.
 */
export function griefsFigureDynamique(brut, dynamique) {
    const griefs = [];
    const dire = (g) => {
        griefs.push(g);
    };
    if (planDynamique(brut) === null) {
        dire('une figure dynamique est une figure du plan, qui se dessine');
        return griefs;
    }
    const objets = objetsBruts(brut);
    const points = pointsNommes(brut);
    const mobiles = new Set();
    for (const p of dynamique.points) {
        if (mobiles.has(p.nom))
            dire(`le point ${p.nom} est déclaré deux fois`);
        mobiles.add(p.nom);
        const ici = points.get(p.nom);
        if (ici === undefined) {
            dire(`le point ${p.nom} n’est pas un point nommé de la figure`);
            continue;
        }
        if (p.sur === undefined)
            continue;
        const rang = rangDe(p.sur);
        const o = rang === null ? undefined : objets[rang];
        if (o === undefined) {
            dire(`${p.nom} : « ${p.sur} » ne désigne aucun objet de la figure`);
            continue;
        }
        if (o.type !== 'cercle' && o.type !== 'segment' && o.type !== 'droite') {
            dire(`${p.nom} : un point se déplace sur un cercle, un segment ou une droite`);
            continue;
        }
        if (nomsCites(o).includes(p.nom)) {
            dire(`${p.nom} : l’objet qui le porte ne peut pas dépendre de lui`);
            continue;
        }
        const q = projeterSur(brut, p.sur, ici, points);
        if (q === null || Math.hypot(q.x - ici.x, q.y - ici.y) > 1e-3) {
            dire(`${p.nom} : au départ, le point n’est pas sur l’objet « ${p.sur} »`);
        }
    }
    objets.forEach((o, k) => {
        const cites = nomsCites(o).filter((n) => mobiles.has(n));
        if (cites.length === 0)
            return;
        if (o.type === 'cote') {
            dire(`objets[${k}] : une cote écrite ne suit pas ${cites.join(', ')} ; déclare une mesure`);
        }
        if (o.type === 'angle' && (o.droit === true || typeof o.texte === 'string')) {
            dire(`objets[${k}] : un angle marqué ou écrit ne suit pas ${cites.join(', ')} ; déclare une mesure`);
        }
    });
    for (const m of dynamique.mesures ?? []) {
        const noms = m.type === 'longueur' ? [m.de, m.a] : m.type === 'angle' ? [m.de, m.sommet, m.a] : [m.point];
        const absents = noms.filter((n) => !points.has(n));
        if (absents.length > 0)
            dire(`une mesure cite un point absent : ${absents.join(', ')}`);
        else if (new Set(noms).size !== noms.length)
            dire('une mesure cite deux fois le même point');
    }
    return griefs;
}
//# sourceMappingURL=figure-dynamique.js.map